import { useState } from 'react';
import {
  Button, Grid2 as Grid, Typography, Paper, styled,
  FormControl, MenuItem, InputLabel, Select, TextField
} from "@mui/material";
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import PropTypes from 'prop-types';
import { isEmpty } from "../common/helpers";

import { useSelector } from 'react-redux';

const CardInfo = styled(Paper)(() => ({
  backgroundColor: "#F1F1F1",
  borderRadius: 8,
  paddingTop: 35,
  paddingBottom: 35,
  paddingRight: 30,
  paddingLeft: 30,
}));

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

const TransferenciasWS = ({
  servicio, setServicio, empresa, setEmpresa,
  monto, setMonto,
  concepto, setConcepto,
  uploadFile, loading
}) => {
  const empresas = useSelector(state => state.empresas.empresas);

  const filteredServicios = empresa && !isEmpty(empresas)
    ? (() => {
      const selectedEmpresaObj = empresas.find(e => e._id === empresa);
      if (!selectedEmpresaObj) return [];
      const empresaServicios = selectedEmpresaObj.servicios;
      return empresaServicios;
    })()
    : [];

  const [files, setFiles] = useState([]);

  const handleFileChange = (event) => {
    const selectedFiles = Array.from(event.target.files);
    setFiles((prevFiles) => [...prevFiles, ...selectedFiles]);
  };

  const handleRemoveFile = (index) => {
    setFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));
  };

  return (
    <CardInfo>
      <Typography className="titleMain" variant="h3" component="h2">Transferencias bancarias</Typography>
      <Typography className="subtitleMain" mt={1} mb={2}>
        También podrás hacer uso de transferencias interbancarias con los siguientes datos como método de pago.
      </Typography>

      <Typography sx={{ color: '#0E2465', marginBottom: 2 }}>Concepto de Pago:</Typography>

      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel id="empresa-label">Empresa</InputLabel>
        <Select
          labelId="empresa-label"
          id="empresas"
          value={empresa}
          label="Empresa"
          onChange={(e) => {
            setEmpresa(e.target.value);
          }}
        >
          {
            !isEmpty(empresas) ?
              empresas.filter(info => info.status === true).map(info => (
                <MenuItem key={info._id} value={info._id}>{info.razon}</MenuItem>
              ))
              : (
                <MenuItem value="">Sin empresas disponibles</MenuItem>
              )
          }
        </Select>
      </FormControl>

      <FormControl fullWidth>
        <InputLabel id="servicio-label">Servicios disponibles</InputLabel>
        <Select
          labelId="servivio-label"
          id="servicios"
          value={servicio}
          label="Servicios disponibles"
          onChange={(e) => setServicio(e.target.value)}
          disabled={!empresa}
        >
          {
            !isEmpty(filteredServicios) ?
              filteredServicios.map(info => (
                <MenuItem key={info} value={info}>{info}</MenuItem>
              ))
              : (
                <MenuItem value="">Seleccionar...</MenuItem>
              )
          }
        </Select>
      </FormControl>

      <TextField
        fullWidth
        label="Concepto"
        value={concepto}
        onChange={(e) => setConcepto(e.target.value)}
        sx={{ mt: 2 }}
        required
      />

      <TextField
        fullWidth
        label="Monto pagado"
        type="number"
        value={monto}
        onChange={(e) => setMonto(e.target.value)}
        sx={{ mt: 2 }}
        required
        slotProps={{
          input: {
            startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>
          },
        }}
      />

      <hr className="separator" />
      <Grid container spacing={3} sx={{ my: 2 }}>
        <Grid size={{ sm: 7 }} mt={2}>
          <Typography className="subtitleMain">
            <small>
              Al realizar la transferencia, adjunta tus comprobantes aquí para validar tu inversión.
            </small>
          </Typography>
        </Grid>
        <Grid size={{ xs: 12, sm: 5 }} mt={2}>
          <Button
            component="label"
            variant="outlined"
            tabIndex={-1}
            endIcon={<CloudUploadIcon />}
            fullWidth
            disabled={isEmpty(servicio)}
          >
            Adjuntar
            <VisuallyHiddenInput
              type="file"
              accept=".pdf, .png, .jpg, .jpeg"
              multiple
              onChange={handleFileChange}
            />
          </Button>
        </Grid>

        {files.length > 0 && (
          <Grid size={12}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Archivos seleccionados:</Typography>
            <Grid container spacing={2}>
              {files.map((file, index) => (
                <Grid key={index} size={{ xs: 6, sm: 4, md: 3 }} sx={{ position: 'relative' }}>
                  <Paper variant="outlined" sx={{ p: 1, textAlign: 'center', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                    {file.type.startsWith('image/') ? (
                      <img
                        src={URL.createObjectURL(file)}
                        alt={file.name}
                        style={{ maxWidth: '100%', maxHeight: '60px', objectFit: 'contain', marginBottom: '4px' }}
                      />
                    ) : (
                      <Typography variant="body2" sx={{ mb: 1 }}>{file.name.substring(file.name.lastIndexOf('.') + 1).toUpperCase()}</Typography>
                    )}
                    <Typography variant="caption" noWrap sx={{ width: '100%', display: 'block' }}>
                      {file.name}
                    </Typography>
                    <Button
                      size="small"
                      color="error"
                      onClick={() => handleRemoveFile(index)}
                      sx={{ mt: 1, minWidth: 'auto', p: 0.5 }}
                    >
                      Quitar
                    </Button>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Grid>
        )}

        <Grid size={12} mt={2}>
          <Button
            variant="contained"
            fullWidth
            disabled={files.length === 0 || loading || !monto || Number(monto) <= 0 || !concepto.trim()}
            loading={loading}
            onClick={() => uploadFile(files)}
          >
            Subir autorizaciones
          </Button>
        </Grid>
      </Grid>
    </CardInfo >
  )
}

TransferenciasWS.propTypes = {
  uploadFile: PropTypes.func.isRequired,
  empresas: PropTypes.array,
  servicio: PropTypes.string.isRequired,
  setServicio: PropTypes.func.isRequired,
  monto: PropTypes.string.isRequired,
  setMonto: PropTypes.func.isRequired,
  concepto: PropTypes.string.isRequired,
  setConcepto: PropTypes.func.isRequired,
}

export default TransferenciasWS